import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { tradeInvoiceAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Loader2, UploadCloud, CheckCircle2, FileText, MapPin, Calendar, Hash,
  Building2, ShieldCheck, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function TradeInvoiceUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    tradeInvoiceAPI.info(token)
      .then((res) => setInfo(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await tradeInvoiceAPI.upload(token, fd);
      setDone(true);
      toast.success('Invoice uploaded — thank you!');
      tradeInvoiceAPI.info(token).then((res) => setInfo(res.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="surface max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-rose-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold mb-2">Link not found</h1>
            <p className="text-muted-foreground text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="trade-invoice-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Brand header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-2xl font-bold">
            <span>{info.company_name}</span>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Upload your invoice for this purchase order</p>
        </div>

        {/* PO summary */}
        <Card className="surface">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl brand-gradient flex items-center justify-center shadow-glow">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-lg">{info.job_title || 'Job'}</h2>
                  <p className="text-xs text-muted-foreground">For {info.contractor_name || 'you'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">PO total</p>
                <p className="text-lg font-bold font-mono">${Number(info.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <Row icon={Hash} label="PO Number" value={info.po_number} mono />
              {info.job_address && <Row icon={MapPin} label="Site" value={info.job_address} />}
              {info.on_site_date && <Row icon={Calendar} label="On-site" value={format(new Date(info.on_site_date), 'EEE, d MMM yyyy')} />}
              <Row icon={Building2} label="Builder" value={info.company_name} />
            </div>

            {(info.items || []).length > 0 && (
              <div className="mt-4 rounded-lg border border-border overflow-hidden">
                {info.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-xs border-b border-border last:border-0">
                    <span className="text-muted-foreground">{it.description}</span>
                    <span className="font-mono">{it.quantity} × ${Number(it.unit_price || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload */}
        <Card className="surface">
          <CardContent className="p-6">
            {done ? (
              <div className="text-center py-6" data-testid="upload-success">
                <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold">Invoice received</h3>
                <p className="text-muted-foreground text-sm mt-1">Thanks! {info.company_name} has your invoice. You can upload another if needed.</p>
                <Button variant="outline" className="mt-4" onClick={() => setDone(false)} data-testid="upload-another-btn">
                  Upload another
                </Button>
              </div>
            ) : (
              <>
                <h3 className="font-semibold mb-1">Upload your invoice</h3>
                <p className="text-xs text-muted-foreground mb-4">PDF, image, Word or Excel. It goes straight to the builder — no account needed.</p>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full border-2 border-dashed border-border rounded-xl py-10 flex flex-col items-center gap-2 hover:border-primary/60 hover:bg-secondary/30 transition-colors"
                  data-testid="upload-dropzone"
                >
                  {uploading ? (
                    <><Loader2 className="h-8 w-8 animate-spin text-primary" /><span className="text-sm">Uploading…</span></>
                  ) : (
                    <><UploadCloud className="h-8 w-8 text-primary" /><span className="text-sm font-medium">Click to choose your invoice file</span></>
                  )}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  data-testid="upload-file-input"
                />
              </>
            )}

            {(info.uploaded_invoices || []).length > 0 && (
              <div className="mt-5 pt-4 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground mb-2">Already uploaded</p>
                <div className="space-y-1.5">
                  {info.uploaded_invoices.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                      <span className="truncate">{d.filename}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Secure upload link
          {info.expires_at ? ` · expires ${format(new Date(info.expires_at), 'd MMM yyyy')}` : ''} · Powered by My Project Agent
        </p>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-center gap-2 text-muted-foreground">
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span>{label}:</span>
    <span className={`${mono ? 'font-mono' : ''} text-foreground truncate`}>{value || '—'}</span>
  </div>
);
